import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, UtensilsCrossed, ChefHat } from "lucide-react";

const check = "repeating-conic-gradient(#c8102e 0 25%, #fff 0 50%) 0 0/2.2em 2.2em";
const flag = "linear-gradient(90deg,#009246 0 33%,#fff 0 66%,#ce2b37 0)";

export const MENU_THEMES = [
  { id: 1, name: "Klasik", outer: "#6b3a12", page: "#fbf3df", line: "#c9a24a", title: "#1c1917", text: "#44403c", pill: "#a3121a", pillText: "#fff", pillBorder: "#d4af37", round: true },
  { id: 2, name: "Dërrasë e zezë", outer: "#2b1d12", page: "#151515", line: "#c9a24a", title: "#ffffff", text: "#d6d3d1", pill: "#c1121f", pillText: "#fff", pillBorder: "transparent", round: true, flag: true },
  { id: 3, name: "Trattoria", outer: check, page: "#f6ecd6", line: "#d6c3a0", title: "#1c1917", text: "#57534e", pill: "#b91c1c", pillText: "#fff", pillBorder: "transparent", round: true, chef: true },
  { id: 4, name: "Modern", outer: "#3f0d12", page: "#ffffff", line: "#e7e5e4", title: "#1c1917", text: "#57534e", pill: "#8b1022", pillText: "#fff", pillBorder: "transparent", slant: true, wave: true },
  { id: 5, name: "Gjelbër", outer: "#0b2418", page: "#143d2b", line: "#c9a24a", title: "#ffffff", text: "#d1fae5", pill: "#e9c46a", pillText: "#1c1917", pillBorder: "#b08d2c", round: true },
];

export const themeOf = (id) => MENU_THEMES.find((t) => t.id === Number(id)) || MENU_THEMES[0];
const money = (p) => (String(p).match(/[€$£a-zA-Z]/) ? p : `${p} €`);

function Item({ it, t }) {
  const shape = t.slant ? { clipPath: "polygon(0 8%,100% 0,100% 92%,0 100%)", borderRadius: "0.3em" } : { borderRadius: "50%" };
  return (
    <div className="flex-1 min-h-0 flex flex-col items-center text-center px-[0.4em]">
      <div className="h-[48%] aspect-square shrink-0 overflow-hidden shadow-[0_0.3em_0.6em_rgba(0,0,0,.35)]" style={{ ...shape, width: t.slant ? "90%" : undefined, aspectRatio: t.slant ? "auto" : "1" }}>
        {it.image ? <img src={it.image} alt={it.name} draggable={false} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center" style={{ background: t.line }}><UtensilsCrossed className="w-1/3 h-1/3 text-white/80" /></div>}
      </div>
      <div className="mt-[0.35em] font-black italic leading-tight line-clamp-2" style={{ color: t.title, fontFamily: "'Playfair Display', Georgia, serif", fontSize: "1.25em" }}>{it.name}</div>
      {it.desc && <div className="leading-snug line-clamp-2 mt-[0.15em]" style={{ color: t.text, fontSize: "0.72em" }}>{it.desc}</div>}
      {it.price && <div className="mt-[0.3em] px-[0.8em] py-[0.1em] rounded-full font-black" style={{ background: t.pill, color: t.pillText, border: `0.12em solid ${t.pillBorder}`, fontSize: "1.05em" }}>{money(it.price)}</div>}
    </div>
  );
}

function Page({ pg, t, side, logo, title }) {
  const frame = { background: t.page, borderColor: t.line };
  return (
    <div className={`absolute inset-0 overflow-hidden flex flex-col ${side === "l" ? "rounded-l-[0.6em]" : "rounded-r-[0.6em]"}`} style={{ ...frame, boxShadow: side === "l" ? "inset -1.2em 0 1.2em -1em rgba(0,0,0,.45)" : "inset 1.2em 0 1.2em -1em rgba(0,0,0,.45)" }}>
      <div className="absolute inset-[0.5em] border-[0.1em] rounded-[0.4em] pointer-events-none" style={{ borderColor: t.line }} />
      {t.flag && <div className="absolute top-[0.9em] left-1/2 -translate-x-1/2 w-1/3 h-[0.3em] rounded-full" style={{ background: flag }} />}
      {t.wave && <div className="absolute -bottom-[1.6em] -left-[10%] w-[120%] h-[2.6em] rounded-[50%]" style={{ background: t.pill, opacity: 0.9 }} />}
      {pg?.cover ? (
        <div className="relative flex-1 flex flex-col items-center justify-center gap-[0.6em] p-[1em] text-center">
          {t.chef && <ChefHat className="w-[2.4em] h-[2.4em]" style={{ color: t.pill }} />}
          {logo ? <img src={logo} alt="" className="w-[45%] aspect-square object-cover rounded-full border-[0.2em] shadow-lg bg-white" style={{ borderColor: t.line }} /> : <UtensilsCrossed className="w-[3em] h-[3em]" style={{ color: t.line }} />}
          <div className="font-black italic tracking-wide" style={{ color: t.title, fontFamily: "'Playfair Display', Georgia, serif", fontSize: "2.6em" }}>Meny</div>
          <div className="w-1/2 h-[0.12em]" style={{ background: t.line }} />
          <div className="font-bold line-clamp-2" style={{ color: t.text, fontSize: "0.95em" }}>{title}</div>
        </div>
      ) : pg?.items ? (
        <div className="relative flex-1 min-h-0 flex flex-col py-[1.1em] gap-[0.4em]">
          {pg.cat && <div className="text-center font-bold uppercase tracking-widest truncate px-[1em] mt-[0.3em]" style={{ color: t.line, fontSize: "0.62em" }}>{pg.cat}</div>}
          {pg.items.map((it, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div className="mx-[12%] h-[0.08em] shrink-0" style={{ background: t.line }} />}
              <Item it={it} t={t} />
            </React.Fragment>
          ))}
        </div>
      ) : (
        <div className="relative flex-1 flex items-center justify-center font-black italic" style={{ color: t.line, fontFamily: "'Playfair Display', Georgia, serif", fontSize: "1.4em" }}>Ju bëftë mirë!</div>
      )}
    </div>
  );
}

const buildPages = (items) => {
  const pages = [{ cover: true }];
  for (let i = 0; i < items.length; i += 2) {
    const chunk = items.slice(i, i + 2);
    pages.push({ items: chunk, cat: chunk[0].cat });
  }
  if (pages.length % 2) pages.push({ end: true });
  return pages;
};

export function MenuBook({ items, theme, logo, title, width, onOpen, single }) {
  const t = themeOf(theme);
  const pages = buildPages(items);
  const [p, setP] = useState(0);
  const [flip, setFlip] = useState(null);
  const touch = useRef(null);
  const step = single ? 1 : 2;
  const h = single ? width * 1.4 : width / 1.5;
  const go = (dir) => {
    if (flip) return;
    const n = p + dir * step;
    if (n < 0 || n >= pages.length) return;
    setFlip(dir);
  };
  const done = () => { setP((x) => x + flip * step); setFlip(null); };
  const pp = { t, logo, title };
  const handlers = {
    onTouchStart: (e) => { touch.current = e.touches[0].clientX; },
    onTouchEnd: (e) => { const dx = e.changedTouches[0].clientX - (touch.current ?? 0); if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1); else if (onOpen) onOpen(); },
    onClick: (e) => { if (onOpen && e.detail && !("ontouchstart" in window)) onOpen(); },
  };
  if (single) {
    const leaf = flip === 1 ? pages[p] : flip === -1 ? pages[p - 1] : null;
    const base = flip === 1 ? pages[p + 1] : pages[p];
    return (
      <div data-testid="menu-book" className="relative select-none mx-auto" style={{ width, height: h, fontSize: width / 21, perspective: width * 3, touchAction: "pan-y" }} {...handlers}>
        <div className="absolute -inset-[0.4em] rounded-[0.9em] shadow-2xl" style={{ background: t.outer }} />
        <div className="absolute inset-0"><Page pg={base} side="r" {...pp} /></div>
        {leaf && (
          <motion.div className="absolute inset-0" style={{ transformOrigin: "left center", zIndex: 5, backfaceVisibility: "hidden" }}
            initial={{ rotateY: flip === 1 ? 0 : -180 }} animate={{ rotateY: flip === 1 ? -180 : 0 }} transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }} onAnimationComplete={done}>
            <Page pg={leaf} side="r" {...pp} />
          </motion.div>
        )}
        <BookNav p={p} total={pages.length} go={go} step={1} />
      </div>
    );
  }
  const L = flip === -1 ? pages[p - 2] : pages[p];
  const R = flip === 1 ? pages[p + 3] : pages[p + 1];
  const front = flip === 1 ? pages[p + 1] : pages[p];
  const back = flip === 1 ? pages[p + 2] : pages[p - 1];
  return (
    <div data-testid="menu-book" className="relative select-none mx-auto" style={{ width, height: h, fontSize: width / 42, perspective: width * 2.5, touchAction: "pan-y" }} {...handlers}>
      <div className="absolute -inset-[0.5em] rounded-[0.9em] shadow-2xl" style={{ background: t.outer }} />
      <div className="absolute inset-y-0 left-0 w-1/2"><Page pg={L} side="l" {...pp} /></div>
      <div className="absolute inset-y-0 right-0 w-1/2"><Page pg={R} side="r" {...pp} /></div>
      {flip && (
        <motion.div className="absolute inset-y-0 w-1/2" style={{ [flip === 1 ? "right" : "left"]: 0, transformStyle: "preserve-3d", transformOrigin: flip === 1 ? "left center" : "right center", zIndex: 5 }}
          initial={{ rotateY: 0 }} animate={{ rotateY: flip === 1 ? -180 : 180 }} transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }} onAnimationComplete={done}>
          <div className="absolute inset-0" style={{ backfaceVisibility: "hidden" }}><Page pg={front} side={flip === 1 ? "r" : "l"} {...pp} /></div>
          <div className="absolute inset-0" style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}><Page pg={back} side={flip === 1 ? "l" : "r"} {...pp} /></div>
        </motion.div>
      )}
      <div className="absolute inset-y-0 left-1/2 w-[0.25em] -translate-x-1/2 z-10 pointer-events-none" style={{ background: "linear-gradient(90deg,rgba(0,0,0,.35),rgba(0,0,0,.05),rgba(0,0,0,.35))" }} />
      <BookNav p={p} total={pages.length} go={go} />
    </div>
  );
}

function BookNav({ p, total, go, step = 2 }) {
  const stop = (e) => e.stopPropagation();
  return (
    <>
      {p > 0 && <button data-testid="menu-book-prev" onTouchEnd={stop} onClick={(e) => { stop(e); go(-1); }} aria-label="Mbrapa" className="absolute left-[0.3em] top-1/2 -translate-y-1/2 z-20 w-[2.4em] h-[2.4em] rounded-full bg-black/45 text-white flex items-center justify-center"><ChevronLeft className="w-[1.4em] h-[1.4em]" /></button>}
      {p + step < total && <button data-testid="menu-book-next" onTouchEnd={stop} onClick={(e) => { stop(e); go(1); }} aria-label="Përpara" className="absolute right-[0.3em] top-1/2 -translate-y-1/2 z-20 w-[2.4em] h-[2.4em] rounded-full bg-black/45 text-white flex items-center justify-center"><ChevronRight className="w-[1.4em] h-[1.4em]" /></button>}
      <div data-testid="menu-book-page" className="absolute -bottom-[2.2em] inset-x-0 text-center text-slate-400 font-semibold" style={{ fontSize: "0.9em" }}>{Math.floor(p / step) + 1} / {Math.ceil(total / step)}</div>
    </>
  );
}

export function MenuBookViewer({ onClose, ...props }) {
  const [w, setW] = useState(0);
  const [single, setSingle] = useState(false);
  useEffect(() => {
    const fit = () => {
      const portrait = window.innerHeight > window.innerWidth && window.innerWidth < 700;
      setSingle(portrait);
      setW(portrait ? Math.min(window.innerWidth - 32, (window.innerHeight - 150) / 1.4) : Math.min(window.innerWidth - 24, (window.innerHeight - 140) * 1.5, 1100));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);
  return createPortal(
    <div className="fixed inset-0 z-[2000] bg-black flex flex-col items-center justify-center" data-testid="menu-book-viewer">
      <button onClick={onClose} data-testid="menu-book-close" aria-label="Mbyll" className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 text-white flex items-center justify-center"><X className="w-5 h-5" /></button>
      {w > 0 && <MenuBook key={single ? "s" : "d"} width={w} single={single} {...props} />}
      {single && <p className="text-white/50 text-xs mt-12">Shfletoje me gisht · ktheje telefonin për dy faqe</p>}
    </div>,
    document.body
  );
}

export function MenuStylePicker({ value, onChange, onPreview, hasItems = true }) {
  return (
    <div data-testid="menu-style-picker">
      <div className="text-sm font-bold text-slate-700 mb-2">Zgjidh modelin e menysë (libër)</div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
        {MENU_THEMES.map((t) => (
          <button key={t.id} type="button" data-testid={`menu-style-${t.id}`} onClick={() => onChange(t.id)}
            className={`shrink-0 w-24 rounded-xl p-1.5 border-2 transition-colors ${Number(value) === t.id ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white"}`}>
            <div className="h-14 rounded-lg p-1 flex gap-0.5" style={{ background: t.outer }}>
              {[0, 1].map((i) => (
                <div key={i} className="flex-1 rounded-sm flex flex-col items-center justify-center gap-0.5" style={{ background: t.page, border: `1px solid ${t.line}` }}>
                  <span className="w-3 h-3 rounded-full" style={{ background: t.slant ? t.line : "#e2a24a" }} />
                  <span className="w-5 h-1 rounded-full" style={{ background: t.pill }} />
                </div>
              ))}
            </div>
            <div className="text-[11px] font-bold text-slate-700 mt-1 truncate">{t.name}</div>
          </button>
        ))}
      </div>
      {onPreview && hasItems && <button type="button" data-testid="menu-style-preview" onClick={onPreview} className="mt-2 text-sm font-semibold text-emerald-700 underline">Shiko si duket menyja</button>}
    </div>
  );
}

export const bookEligible = (cat, sub) => cat === "ushqim" || (cat === "reklame" && /ushqim/i.test(sub || ""));

export default MenuBook;
